import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus, Shield, Users, Zap, Radio, Trash } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [rules, setRules] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ title: "", directive: "", weight: 7 });
  const [demoUsers, setDemoUsers] = useState([]);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupBusy, setCleanupBusy] = useState(false);

  const loadAll = async () => {
    const [s, r, u, d] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/rules"),
      api.get("/admin/users"),
      api.get("/admin/demo-users").catch(() => ({ data: { count: 0, users: [] } })),
    ]);
    setStats(s.data);
    setRules(r.data.rules);
    setUsers(u.data.users);
    setDemoUsers(d.data.users || []);
  };

  useEffect(() => { loadAll(); }, []);

  const createRule = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/rules", form);
      setForm({ title: "", directive: "", weight: 7 });
      toast.success("Preset rule armed");
      loadAll();
    } catch { toast.error("Failed"); }
  };
  const delRule = async (id) => {
    await api.delete(`/admin/rules/${id}`);
    toast("Rule removed");
    loadAll();
  };
  const setTier = async (user_id, tier) => {
    await api.post("/admin/subscription", { user_id, tier });
    toast.success(`Set to ${tier}`);
    loadAll();
  };

  const wipeDemoUsers = async () => {
    setCleanupBusy(true);
    try {
      const { data } = await api.post("/admin/demo-users/cleanup", { confirm: "WIPE_DEMO_USERS" });
      const total = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Demo users wiped — ${total} records removed`, {
        description: (data.demo_emails || []).join(", ") || "clean",
      });
      setCleanupOpen(false);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cleanup failed");
    } finally { setCleanupBusy(false); }
  };

  const cells = [
    { label: "Users", value: stats?.total_users, icon: Users },
    { label: "Active Now", value: stats?.active_now, icon: Zap },
    { label: "Pro", value: stats?.pro_users, icon: Shield },
    { label: "Groups", value: stats?.total_groups, icon: Radio },
    { label: "AI Rules", value: stats?.total_rules, icon: Shield },
  ];

  return (
    <div className="px-4 md:px-8 py-6">
      <div className="text-xs tracking-[0.3em] font-mono text-[#00E5FF]">// CONTROL ROOM</div>
      <h1 className="font-[Unbounded] text-3xl md:text-4xl font-bold tracking-tighter mt-2 mb-6">Owner Console</h1>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        {cells.map((c) => (
          <div key={c.label} className="p-4 bg-[#0D0E15] border border-white/10 rounded-xl">
            <c.icon size={16} className="text-[#00E5FF] mb-2" strokeWidth={1.5} />
            <div className="text-[10px] tracking-widest font-mono text-white/40">{c.label.toUpperCase()}</div>
            <div className="font-[Unbounded] text-2xl mt-1">{c.value ?? "—"}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section>
          <div className="text-xs tracking-[0.3em] font-mono text-white/40 mb-3">// PRESET AI RULES</div>
          <form onSubmit={createRule} className="p-5 bg-[#0D0E15] border border-white/10 rounded-2xl space-y-3">
            <input data-testid="rule-title-input" required value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Rule title (e.g. Prioritize shared interests)"
              className="w-full bg-[#05050A] border border-white/10 rounded-lg px-4 py-2.5 focus:border-[#00E5FF] focus:outline-none" />
            <textarea data-testid="rule-directive-input" required value={form.directive}
              onChange={(e) => setForm({ ...form, directive: e.target.value })}
              rows={2}
              placeholder="Directive for AI (e.g. Match users only if they share at least 2 interests)"
              className="w-full bg-[#05050A] border border-white/10 rounded-lg px-4 py-2.5 focus:border-[#00E5FF] focus:outline-none" />
            <div className="flex items-center gap-3">
              <label className="text-[10px] tracking-widest font-mono text-white/50 whitespace-nowrap">WEIGHT · {form.weight}/10</label>
              <input type="range" min={1} max={10} value={form.weight}
                onChange={(e) => setForm({ ...form, weight: parseInt(e.target.value) })}
                className="flex-1 accent-[#00E5FF]" />
              <button data-testid="add-rule-btn" className="inline-flex items-center gap-1 bg-[#00E5FF] text-black text-sm font-semibold px-3 py-2 rounded-lg hover:glow-primary transition-all">
                <Plus size={14} /> Arm
              </button>
            </div>
          </form>

          <div className="mt-4 space-y-2">
            {rules.length === 0 && <p className="text-sm text-white/40">No rules armed. AI will use defaults.</p>}
            {rules.map((r) => (
              <div key={r.id} data-testid={`rule-${r.id}`} className="flex items-start gap-3 p-4 bg-[#0D0E15] border border-white/10 rounded-xl">
                <div className="w-10 text-center">
                  <div className="text-[9px] font-mono text-white/40">W</div>
                  <div className="font-[Unbounded] text-lg text-[#00E5FF]">{r.weight}</div>
                </div>
                <div className="flex-1">
                  <div className="font-semibold">{r.title}</div>
                  <p className="text-sm text-white/60 mt-1">{r.directive}</p>
                </div>
                <button data-testid={`delete-rule-${r.id}`} onClick={() => delRule(r.id)} className="text-white/40 hover:text-[#FF007F]"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </section>

        <section>
          <div className="text-xs tracking-[0.3em] font-mono text-white/40 mb-3">// USER SUBSCRIPTIONS</div>
          <div className="bg-[#0D0E15] border border-white/10 rounded-2xl divide-y divide-white/5">
            {users.map((u) => (
              <div key={u.id} data-testid={`user-row-${u.id}`} className="flex items-center justify-between p-4">
                <div>
                  <div className="font-semibold">{u.display_name} {u.is_admin && <span className="ml-2 text-[9px] font-mono px-1.5 py-0.5 bg-[#FF007F]/20 text-[#FF007F] rounded">ADMIN</span>}</div>
                  <div className="text-xs text-white/40 font-mono">{u.email}</div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid={`tier-btn-${u.id}`} className={`text-xs font-mono px-3 py-1.5 rounded-full border transition-all ${
                      u.subscription_tier === "pro"
                        ? "border-[#39FF14] text-[#39FF14]"
                        : "border-white/20 text-white/60"
                    }`}>
                      {u.subscription_tier?.toUpperCase() || "FREE"}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-[#0D0E15] border-white/10 text-white">
                    <DropdownMenuItem onClick={() => setTier(u.id, "free")} data-testid={`set-free-${u.id}`}>Free</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setTier(u.id, "pro")} data-testid={`set-pro-${u.id}`}>Pro</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Danger zone: seed cleanup */}
      <section className="mt-12 p-5 rounded-2xl border border-[#FF007F]/40 bg-[#FF007F]/[0.05]">
        <div className="flex items-center gap-2">
          <Trash size={14} className="text-[#FF007F]" />
          <div className="text-[10px] tracking-widest font-mono text-[#FF007F]">// DANGER ZONE · SEED CLEANUP</div>
          <span data-testid="demo-users-count" className="ml-auto text-[10px] font-mono uppercase text-white/40">
            {demoUsers.length} demo user{demoUsers.length !== 1 && "s"}
          </span>
        </div>
        <p className="text-xs text-white/60 mt-2">
          Deletes all non-admin <span className="font-mono">@radar.io</span> accounts and their signals, threads, goals, hangouts, approvals, cached matches, and group memberships. Cannot be undone.
        </p>
        {demoUsers.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {demoUsers.map((u) => (
              <span key={u.id} className="text-[10px] font-mono px-2 py-1 rounded-full bg-white/5 border border-white/10 text-white/70">
                {u.email}
              </span>
            ))}
          </div>
        )}
        {!cleanupOpen ? (
          <button
            data-testid="open-cleanup-btn"
            onClick={() => setCleanupOpen(true)}
            disabled={demoUsers.length === 0}
            className="mt-4 inline-flex items-center gap-1.5 border border-[#FF007F]/50 text-[#FF007F] text-xs font-semibold px-4 py-2 rounded-full hover:bg-[#FF007F] hover:text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Trash size={12} /> Wipe demo users
          </button>
        ) : (
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <span className="text-xs text-white/70">Really wipe {demoUsers.length} demo user(s)?</span>
            <button
              data-testid="confirm-cleanup-btn"
              onClick={wipeDemoUsers}
              disabled={cleanupBusy}
              className="inline-flex items-center gap-1.5 bg-[#FF007F] text-white text-xs font-semibold px-4 py-2 rounded-full hover:brightness-110 transition-all disabled:opacity-60"
            >
              <Trash size={12} /> {cleanupBusy ? "Wiping…" : "Yes, wipe"}
            </button>
            <button
              data-testid="cancel-cleanup-btn"
              onClick={() => setCleanupOpen(false)}
              disabled={cleanupBusy}
              className="text-xs text-white/50 hover:text-white px-3 py-2 transition-all"
            >
              Cancel
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
